import {
  AdditiveBlending,
  Color,
  DoubleSide,
  ShaderMaterial
} from 'three';
import { sharedUniforms } from '../core/FrameUniforms.js';
import { noiseGLSL } from '../shaders/lib/noise.glsl.js';

const RING_VERTEX = /* glsl */ `
  uniform float uTime;
  uniform float uSeed;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;

  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vNormalW = normalize(mat3(modelMatrix) * normal);
    vViewW = normalize(cameraPosition - world.xyz);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const RING_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform float uPulse;
  uniform float uOpacity;
  uniform float uFlow;
  uniform float uNoiseScale;
  uniform float uSeed;
  uniform float uShaderIntensity;
  uniform float uGlobalGlow;
  uniform vec3 uColorDeep;
  uniform vec3 uColorEdge;
  uniform vec3 uColorCore;

  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;

  ${noiseGLSL}

  void main() {
    float rim = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewW))), 1.6);
    float filament = 0.5 + 0.5 * sin(vWorld.y * 31.0 + uTime * uFlow * 2.2 + uSeed);
    float noise = 0.5 + 0.5 * snoise(vWorld * uNoiseScale * 0.7 + uTime * 0.6);
    float alpha = (0.42 + rim * 0.68 + filament * 0.2) * (0.72 + noise * 0.28);
    alpha *= uFade * uOpacity;
    if (alpha < 0.004) discard;

    vec3 colour = mix(uColorEdge, uColorCore, clamp(rim * 0.72 + filament * 0.24 + uPulse * 0.2, 0.0, 1.0));
    colour *= uShaderIntensity * uGlobalGlow * (0.78 + uPulse * 0.55);
    gl_FragColor = vec4(colour, alpha);
  }
`;

const GROUND_VERTEX = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorld;

  void main() {
    vUv = uv;
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const GROUND_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform float uReveal;
  uniform float uPulse;
  uniform float uOpacity;
  uniform float uFlow;
  uniform float uSeed;
  uniform float uShaderIntensity;
  uniform float uGlobalGlow;
  uniform vec3 uColorDeep;
  uniform vec3 uColorEdge;
  uniform vec3 uColorCore;

  varying vec2 vUv;
  varying vec3 vWorld;

  ${noiseGLSL}

  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float radius = length(p);
    if (radius > 1.0) discard;

    float angle = atan(p.y, p.x);
    float ringA = exp(-pow((radius - 0.47) / 0.026, 2.0));
    float ringB = exp(-pow((radius - 0.76) / 0.018, 2.0));
    float ringC = exp(-pow((radius - 0.25) / 0.012, 2.0));
    float sectors = 0.54 + 0.46 * smoothstep(-0.1, 0.35,
      sin(angle * 13.0 - uTime * uFlow + uSeed) * 0.5 + 0.5);
    float crawl = 0.5 + 0.5 * snoise(vec3(p * 4.0, uTime * 0.7 + uSeed));
    float sweep = smoothstep(0.76, 1.0,
      sin(angle * 2.0 - uTime * uFlow * 0.8) * 0.5 + 0.5);
    float edge = smoothstep(1.0, 0.74, radius);
    float alpha = (ringA * 0.74 + ringB * 0.92 + ringC * 0.42) * sectors;
    alpha += sweep * ringB * 0.72 + crawl * ringA * 0.16;
    alpha *= edge * uFade * uReveal * uOpacity;
    if (alpha < 0.004) discard;

    vec3 colour = mix(uColorDeep, uColorEdge, clamp(crawl + sweep * 0.38, 0.0, 1.0));
    colour = mix(colour, uColorCore, clamp(sweep * 0.7 + uPulse * 0.3, 0.0, 1.0));
    colour *= uShaderIntensity * uGlobalGlow * (0.76 + uPulse * 0.58);
    gl_FragColor = vec4(colour, alpha);
  }
`;

function uniforms() {
  return sharedUniforms({
    uFade: { value: 1 },
    uReveal: { value: 1 },
    uPulse: { value: 0 },
    uOpacity: { value: 1 },
    uFlow: { value: 1.8 },
    uNoiseScale: { value: 4 },
    uSeed: { value: 0 },
    uShaderIntensity: { value: 1 },
    uColorDeep: { value: new Color('#090413') },
    uColorEdge: { value: new Color('#8b4dce') },
    uColorCore: { value: new Color('#e5c8ff') }
  });
}

export function createCamouflageRingMaterial() {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: uniforms(),
    vertexShader: RING_VERTEX,
    fragmentShader: RING_FRAGMENT
  });
}

export function createCamouflageGroundMaterial() {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: uniforms(),
    vertexShader: GROUND_VERTEX,
    fragmentShader: GROUND_FRAGMENT
  });
}

export function syncCamouflageMaterial(material, config, state = {}) {
  const u = material.uniforms;
  u.uFade.value = state.fade ?? 1;
  u.uReveal.value = state.reveal ?? 1;
  u.uPulse.value = state.pulse ?? 0;
  u.uOpacity.value = (state.opacity ?? 1) * config.opacity;
  u.uFlow.value = config.shaderFlow;
  u.uNoiseScale.value = config.shaderNoiseScale;
  u.uSeed.value = state.seed ?? 0;
  u.uShaderIntensity.value = config.shaderIntensity;
  u.uColorDeep.value.set(config.colorDeep);
  u.uColorEdge.value.set(config.colorEdge);
  u.uColorCore.value.set(config.colorCore);
}
